<script setup lang="ts">
/** Werkzeugleiste des Design-Editors – Optik wie die Kategorie-Leiste im normalen Editor. */
import { FolderOpen, Image, Layers, LayoutGrid, LayoutTemplate, Package, PaintBucket, Sticker, Type } from 'lucide-vue-next'

export type ToolKey = 'product' | 'images' | 'text' | 'stickers' | 'templates' | 'layouts' | 'background' | 'layers' | 'designs'
const props = defineProps<{ active: ToolKey | null; features: { text: boolean; stickers?: boolean; templates: boolean; collage: boolean; backgrounds: boolean; designs?: boolean }; mobile?: boolean }>()
const emit = defineEmits<{ (e: 'select', tool: ToolKey): void }>()

const items = [
  { key: 'product' as const, label: 'Produkt', icon: Package, show: () => true },
  { key: 'images' as const, label: 'Bilder', icon: Image, show: () => true },
  { key: 'text' as const, label: 'Text', icon: Type, show: () => props.features.text },
  { key: 'stickers' as const, label: 'Sticker', icon: Sticker, show: () => !!props.features.stickers },
  { key: 'templates' as const, label: 'Vorlagen', icon: LayoutTemplate, show: () => props.features.templates },
  { key: 'layouts' as const, label: 'Layouts', icon: LayoutGrid, show: () => props.features.collage },
  { key: 'background' as const, label: 'Hintergrund', icon: PaintBucket, show: () => props.features.backgrounds },
  { key: 'layers' as const, label: 'Ebenen', icon: Layers, show: () => true },
  { key: 'designs' as const, label: 'Meine Designs', icon: FolderOpen, show: () => !!props.features.designs },
]
</script>

<template>
  <nav class="rail" :class="{ 'rail--mobile': mobile }" aria-label="Werkzeuge">
    <template v-for="item in items" :key="item.key">
      <button
        v-if="item.show()"
        type="button"
        class="rail__btn"
        :class="{ 'rail__btn--on': active === item.key }"
        :aria-pressed="active === item.key"
        @click="emit('select', item.key)"
      >
        <span class="rail__icon"><component :is="item.icon" :size="mobile ? 20 : 22" :stroke-width="1.75" /></span>
        <span class="rail__label">{{ item.label }}</span>
      </button>
    </template>
  </nav>
</template>

<style scoped>
.rail { display: flex; flex-direction: column; gap: 10px; width: 112px; padding: 18px 12px 24px; background: #fff; border-right: 1px solid rgba(15, 23, 42, 0.06); overflow-y: auto; box-sizing: border-box; }
.rail__btn { display: flex; flex-direction: column; align-items: center; gap: 8px; width: 100%; padding: 10px 6px; border: 1px solid transparent; border-radius: 6px; background: transparent; font: inherit; cursor: pointer; color: rgba(15, 23, 42, 0.78); transition: transform 100ms ease, border-color 100ms ease; }
.rail__btn:hover { transform: translateY(-1px); }
.rail__btn:focus-visible { outline: 2px solid var(--pfe-color-accent, #e30b17); outline-offset: 2px; }
.rail__icon { display: flex; align-items: center; justify-content: center; width: 52px; height: 40px; border-radius: 4px; background: rgba(15, 23, 42, 0.05); color: rgba(15, 23, 42, 0.62); }
.rail__label { font-size: 13px; line-height: 1.2; }
.rail__btn--on { background: #fff; border-color: var(--pfe-color-accent, #e30b17); box-shadow: 0 1px 4px rgba(227, 11, 23, 0.12); }
.rail__btn--on .rail__icon { background: rgba(227, 11, 23, 0.08); color: var(--pfe-color-accent, #e30b17); }
.rail__btn--on .rail__label { color: #0f172a; font-weight: 700; }

.rail--mobile { flex-direction: row; width: auto; gap: 2px; padding: 6px 4px calc(6px + env(safe-area-inset-bottom)); border-right: 0; border-top: 1px solid rgba(15, 23, 42, 0.1); overflow-x: auto; overflow-y: hidden; }
.rail--mobile .rail__btn { flex: 1; min-width: 56px; gap: 3px; padding: 4px 2px; }
.rail--mobile .rail__icon { width: 36px; height: 28px; background: transparent; }
.rail--mobile .rail__label { font-size: 10px; }
.rail--mobile .rail__btn--on { box-shadow: none; border-color: transparent; }
</style>
